<script setup lang="ts">
import { ref, useTemplateRef, computed, onMounted } from 'vue';
import { Button, Column, DataTable, Card, InputText, Menu, MultiSelect } from 'primevue';
import type { DataTableRowDoubleClickEvent, DataTableRowClickEvent } from 'primevue';
import EditProductDialog from '@/products/components/EditProductDialog.vue';
import type { MenuItem } from 'primevue/menuitem';
import DeleteProductDialog from './components/DeleteProductDialog.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import MainLayout from '@/common/components/MainLayout.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useProductAuditApi } from '@/audit/api/productAuditApi';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import type { ProductResponse } from './api/common/productResponse';
import { useProductStore } from 'shared/products/stores/productStore';
import { useRefereshProductStore } from './commands/refreshProductStore';
import { useRefereshProductGroupStore } from './groups/commands/refreshProductGroupStore';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { ProductType } from 'shared/products/domain/productType';
import type { Product } from 'shared/products/domain/product';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore.ts';

const selectedColumnsLocalStorageKey = "product-table-columns";

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const productStore = useProductStore();
const productGroupStore = useProductGroupStore();
const refreshProductStore = useRefereshProductStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const featureFlagStore = useFeatureFlagStore();


const productAuditApi = useProductAuditApi();

const filters = ref({
  global: {
    value: null,
    matchMode: "contains"
  }
});

interface ProductItem extends Product {
  typeName: string;
  nameSortOrder: number;
  productGroupName: string | null;
  isSortedString: string;
}

const products = computed<ProductItem[]>(() => {

  const allowedTypes: ProductType[] = []

  if (featureFlagStore.isLogYardEnabled()) allowedTypes.push(ProductType.Log);
  if (featureFlagStore.isBulkEnabled()) allowedTypes.push(ProductType.Bulk);
  if (featureFlagStore.isCoilEnabled()) allowedTypes.push(ProductType.Coil);

  const result = productStore.products
  .filter(p =>allowedTypes.includes(p.type))
  .map(p => {
    const item: ProductItem = {
      id: p.id,
      name: p.name,
      description: p.description,
      externalCode: p.externalCode,
      quality: p.quality,
      typeName: translations.value.productType(p.type),
      color: p.color,
      nameSortOrder: 0,
      logData: p.logData,
      bulkData: p.bulkData,
      isSortedString: p.logData ? translations.value.generic.yes : translations.value.generic.no,
      coilData: p.coilData,
      productGroupName: null,
      type: p.type,
      productGroupId: p.productGroupId
    };

    if (p.productGroupId) {
      const group = productGroupStore.getById(p.productGroupId);
      item.productGroupName = group?.name ?? null;
    }

    return item;
  });

  result.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    if (item) {
      item.nameSortOrder = i;
    }
  }

  return result;
});

interface ColumnField {
  field: string;
  header: string;
}

const columns = ref<ColumnField[]>([
  { field: 'name', header: translations.value.generic.name },
  { field: 'typeName', header: translations.value.generic.type },
  { field: 'description', header: translations.value.generic.description },
  { field: 'externalCode', header: translations.value.generic.code },
  { field: 'quality', header: translations.value.generic.quality },
  { field: 'productGroupName', header: translations.value.generic.productGroup },
  { field: 'logData.minDiameter', header: translations.value.products.logData.minDiameter },
  { field: 'logData.maxDiameter', header: translations.value.products.logData.maxDiameter },
  { field: 'logData.maxlegth', header: translations.value.products.logData.maxLength },
  { field: 'logData.minLength', header: translations.value.products.logData.minLength },
  { field: 'logData.averageCylinderVolume', header: translations.value.products.logData.averageCylinderVolume },
  { field: 'logData.averagePhysicalVolume', header: translations.value.products.logData.averagePhysicalVolume },
  { field: 'isSortedString', header: translations.value.products.logData.isSorted },
  { field: 'logData.species', header: translations.value.products.logData.species },
  { field: 'logData.logSorterName', header: translations.value.products.logData.logSorterName },
  { field: 'logData.sawLineName', header: translations.value.products.logData.sawLineName },
  { field: 'coilData.diameter', header: translations.value.products.coilData.diameter },
  { field: 'coilData.depth', header: translations.value.products.coilData.depth },
  { field: 'bulkData.maxWeight', header: translations.value.bulk.maxWeight },
  { field: 'bulkData.maxVolume', header: translations.value.bulk.maxVolume },
]);

const defaultColumns: ColumnField[] = [
  { field: 'name', header: translations.value.generic.name },
  { field: 'typeName', header: translations.value.generic.type },
  { field: 'description', header: translations.value.generic.description },
  { field: 'externalCode', header: translations.value.generic.code },
  { field: 'productGroupName', header: translations.value.generic.productGroup },
];

const selectedColumns = ref<ColumnField[]>(columns.value);

const onToggle = (val: { field: string; header: string; }[]) => {
  selectedColumns.value = columns.value.filter(a => val.some(b => b.field == a.field));
  localStorage.setItem(selectedColumnsLocalStorageKey, JSON.stringify(selectedColumns.value));
};

const selectedProducts = ref<ProductItem[]>([]);
const loading = ref(false);
const menuProductId = ref<string>();

const productMenu = useTemplateRef('product-menu');
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');
const auditDialog = useTemplateRef('audit-dialog');
const actionFailedAlert = useActionFailedAlert();

onMounted(() => {
  refreshProductStore.initialize();
  refreshProductGroupStore.initialize();

  const storedColumnsString = localStorage.getItem(selectedColumnsLocalStorageKey);
  if (storedColumnsString) {
    const storedSelectedColumns: ColumnField[] = JSON.parse(storedColumnsString);
    if (storedSelectedColumns) {
      selectedColumns.value = columns.value.filter(a => storedSelectedColumns.some(b => b.field == a.field));
    } else {
      selectedColumns.value = columns.value.filter(a => defaultColumns.some(b => b.field == a.field));
    }
  } else {
    selectedColumns.value = columns.value.filter(a => defaultColumns.some(b => b.field == a.field));
  }
});

const productMenuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const productId = menuProductId.value;
      if (!productId) return;
      showProduct(productId);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const productId = menuProductId.value;
      if (!productId) return;
      deleteDialog.value?.show(productId);
    }
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const productId = menuProductId.value;
      if (!productId) return;

      const response = await productAuditApi.execute({
        id: productId
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({
        audit: response.audit
      });
    }
  }
]);

function onAddNewClick() {
  editDialog.value?.show({});
}

function onProductDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const product = ev.data as ProductResponse;
  showProduct(product.id);
}

function showProduct(productId: string) {
  editDialog.value?.show({ productId });
}

function onProductClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.product-name');
  if (!name) return;
  showProduct(ev.data.id);
}

function toggleProductMenu(ev: Event, productId: string) {
  menuProductId.value = productId;
  productMenu.value?.toggle(ev);
}

function onProductDeleted(productId: string) {
  selectedProducts.value = selectedProducts.value.filter(product => product.id !== productId);
}

function onEditLogyardProducts() {
  const productIds = selectedLogYardProducts.value.map(product => product.id);
  editDialog.value?.editMultipleProducts(productIds, ProductType.Log);
}

function onEditCoilsProducts() {
  const productIds = selectedCoilsProducts.value.map(product => product.id);
  editDialog.value?.editMultipleProducts(productIds, ProductType.Coil);
}

const selectedLogYardProducts = computed(() => {
  const logYardProducts = selectedProducts.value.filter(product => productStore.getById(product.id)?.type == ProductType.Log);
  return logYardProducts;
});

const selectedCoilsProducts = computed(() => {
  const coilProducts = selectedProducts.value.filter(product => productStore.getById(product.id)?.type == ProductType.Coil);
  return coilProducts;
});

</script>

<template>

  <MainLayout>
    <Card class="product-card">
      <template #title>
        <span>{{ translations.products.products }}</span>
        <Button v-if="selectedLogYardProducts.length > 1" class="edit-logyard-button"
          :label="translations.products.actions.editLogYardProducts(selectedLogYardProducts.length)"
          @click="onEditLogyardProducts">
        </Button>
        <Button v-if="selectedCoilsProducts.length > 1" class="edit-coils-button"
          :label="translations.products.actions.editCoilProducts(selectedCoilsProducts.length)"
          @click="onEditCoilsProducts">
        </Button>
        <Button class="add-button" :label="translations.generic.add" @click="onAddNewClick">
        </Button>
        <Button class="group-button">
          <RouterLink to="/productGroups">{{ translations.generic.groups }}</RouterLink>
        </Button>
        <InputText class="search-field" autofocus v-model="filters.global.value"
          :placeholder="translations.generic.search" />
        <MultiSelect class="column-select" :modelValue="selectedColumns" :options="columns" optionLabel="header"
          @update:modelValue="onToggle" display="comma" placeholder="Select Columns" :maxSelectedLabels="0">
          <template #value>
            {{ translations.generic.selectColumns }}
          </template>
        </MultiSelect>
      </template>
      <template #content>
        <DataTable class="product-table" sort-field="name" v-model:selection="selectedProducts" selectionMode="multiple"
          v-model:filters="filters" :value="products" data-key="id" scrollable scrollHeight="flex" meta-key-selection
          @row-dblclick="onProductDoubleClick" :loading="loading" @row-click="onProductClick" :reorderableColumns="true"
          stateStorage="local" stateKey="product-table-state">
          <template #empty>{{ translations.products.noProductsFound }}</template>
          <template #loading>{{ translations.products.loadingProductData }}</template>
          <Column selectionMode="multiple" headerStyle="width: 3rem" column-key="multiple-select"></Column>
          <Column v-for="(col, index) of selectedColumns" sortable :field="col.field" :header="col.header"
            :key="col.field + '_' + index"></Column>
          <Column :header="translations.generic.color" column-key="color">
            <template #body="product">
              <div v-if="product.data.color" class="flex gap-2">
                <div class="w-6 h-6 rounded" :style="{ backgroundColor: `${product.data.color}` }">
                </div>
              </div>
            </template>
          </Column>
          <Column class="w-24 text-end!" column-key="options-btn">
            <template #body="product">
              <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
                @click="(ev) => toggleProductMenu(ev, product.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </MainLayout>

  <EditProductDialog ref="edit-dialog"></EditProductDialog>
  <DeleteProductDialog ref="delete-dialog" @deleted="onProductDeleted"></DeleteProductDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="product-menu" :model="productMenuItems" :popup="true" />

</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto auto auto auto 1rem auto auto;
  column-gap: 0.5rem;
  align-items: center;
}

.edit-logyard-button {
  grid-column: 3;
}

.edit-coils-button {
  grid-column: 4;
}

.add-button {
  grid-column: 5;
}

.group-button {
  grid-column: 6;
}

.search-field {
  grid-column: 8;
}

.column-select {
  grid-column: 9;
  font-size: small;
  font-weight: inherit;
}

:deep(.product-name) {
  cursor: pointer;
}

:deep(.product-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.product-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
